import { AloftPrice, ToolkitListingID, isUndefined, syntaxFNMA } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence } from '../orderSelectors.ts';
import { transactionToToolkitListingID } from '../utils/transactionToToolkitListingID.ts';
import { selectEditablePropertyDataEntities } from './editablePropertyDataSelectors.ts';

export const selectTransactionEntities = createSelector(
  [selectEditablePropertyDataEntities],
  (editablePropertyDataEntities) =>
    Object.fromEntries(
      Object.entries(editablePropertyDataEntities).map(([id, editableData]) => [
        id,
        { id, ...editableData.transaction },
      ]),
    ),
);

export const selectTransactionById = createSelector(
  [selectTransactionEntities, (_state, id: string) => id],
  (transactionEntities, id) => transactionEntities[id],
);

export const selectAllTransactions = createSelector(
  [selectTransactionEntities],
  (transactionEntities) => Object.values(transactionEntities),
);

export const selectToolkitListingID = createSelector(
  [selectTransactionEntities, (_state, id: string) => id],
  (transactionEntities, id): ToolkitListingID | undefined =>
    transactionToToolkitListingID(transactionEntities[id]),
);

export const selectContractDate = createSelector(
  [selectTransactionById],
  (transaction) => transaction?.contractDate,
);

export const selectSaleDate = createSelector(
  [selectTransactionById],
  (transaction) => transaction?.saleDate,
);

export const selectSalePrice = createSelector(
  [selectTransactionById],
  (transaction) => transaction?.price,
);

const selectSalePrices = createSelector(
  [selectTransactionEntities, selectCompSequence],
  (transactions, compSequence) =>
    Object.fromEntries(['subject', ...compSequence].map((id) => [id, transactions[id]?.price])),
);

export const selectSalePriceDisplay = createSelector([selectSalePrice], (price) => {
  if (isUndefined(price)) return 'N/A';
  return AloftPrice.schema.parse(price).toString(true);
});

export const selectSalePricesDisplay = createSelector([selectSalePrices], (salePrices) =>
  Object.fromEntries(
    Object.entries(salePrices).map(([id, price]) => [
      id,
      price ? AloftPrice.schema.parse(price).toString(true) : 'N/A',
    ]),
  ),
);

export const selectListingStatus = createSelector(
  [selectTransactionById],
  (transaction) => transaction?.status,
);

export const selectConcessionsDisplay = createSelector(
  [selectTransactionById],
  (transaction) => transaction?.concessions ?? 'N/A',
);

export const selectTermsOfSaleDisplay = createSelector(
  [selectTransactionById],
  (transaction) => transaction?.termsOfSale ?? 'N/A',
);

export const selectDateOfSaleDisplay = createSelector([selectTransactionById], (transaction) => {
  // Syntax encoding fails if no status provided
  if (!transaction?.status) return '';

  return syntaxFNMA.dateOfSaleSyntax.encode({
    listingStatus: transaction?.status,
    closeMonth: transaction?.saleDate,
    contractMonth: transaction?.contractDate,
    withdrawalMonth: transaction?.withdrawalDate,
    expirationMonth: transaction?.expirationDate,
  });
});
